import gql from 'graphql-tag';

/**
 * Recently-edited content across the major public types, sorted by updatedAt.
 * Powers the "Recently Edited" picker in the cache-clear dropdown so editors
 * can purge a specific article/page/event/product/newsletter without leaving
 * the dropdown or knowing its URL by heart.
 *
 * `liveOnly: false` keeps drafts visible — editors are often clearing right
 * after a publish, and draft items will simply have no canonicalUrl, which
 * the UI uses to disable clearing on those rows.
 */
export const CACHE_RECENT_CONTENT = gql`
  query CacheRecentContent($perPage: Integer!) {
    search(
      itemTypes: [ARTICLE, PAGE, EVENT, PRODUCT, NEWSLETTER_ISSUE]
      sortStrings: ["updatedAt desc"]
      liveOnly: false
      perPage: $perPage
      page: 1
    ) {
      itemType
      item {
        ... on Article {
          id
          oid
          title
          canonicalUrl
          updatedAt
        }
        ... on Page {
          id
          oid
          title
          canonicalUrl
          updatedAt
        }
        ... on Event {
          id
          oid
          title
          canonicalUrl
          updatedAt
        }
        ... on Product {
          id
          oid
          name
          canonicalUrl
          updatedAt
        }
        ... on NewsletterIssue {
          id
          oid
          title
          canonicalUrl
          updatedAt
        }
      }
    }
  }
`;

/** Number of items requested for the picker — small enough to scan quickly. */
export const CACHE_RECENT_CONTENT_LIMIT = 12;

export type RecentContentKind = 'Article' | 'Page' | 'Event' | 'Product' | 'NewsletterIssue';

export interface NormalizedRecentItem {
  key: string;
  kind: RecentContentKind;
  kindLabel: string;
  title: string;
  canonicalUrl: string | null;
  updatedAt: string | null;
}

const KIND_LABELS: Record<RecentContentKind, string> = {
  Article: 'Article',
  Page: 'Page',
  Event: 'Event',
  Product: 'Product',
  NewsletterIssue: 'Newsletter',
};

/**
 * Flatten the typed search result union into a uniform shape the picker can
 * render without caring about the underlying GraphQL type.
 */
export function normalizeRecentItems(rawSearch: unknown): NormalizedRecentItem[] {
  if (!Array.isArray(rawSearch)) return [];

  const seen = new Set<string>();
  const out: NormalizedRecentItem[] = [];

  for (const row of rawSearch) {
    const item = row?.item;
    if (!item || typeof item !== 'object') continue;

    const typename = item.__typename as RecentContentKind | undefined;
    if (!typename || !(typename in KIND_LABELS)) continue;

    const id = typeof item.id === 'string' ? item.id : String(item.id ?? '');
    if (!id) continue;
    const key = `${typename}:${id}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const rawTitle = typename === 'Product' ? item.name : item.title;
    const title = typeof rawTitle === 'string' && rawTitle.trim() ? rawTitle : '(untitled)';

    const rawUrl = item.canonicalUrl;
    const canonicalUrl = typeof rawUrl === 'string' && rawUrl
      ? rawUrl
      : (rawUrl ? String(rawUrl) : null);

    const rawUpdatedAt = item.updatedAt;
    const updatedAt = typeof rawUpdatedAt === 'string' && rawUpdatedAt
      ? rawUpdatedAt
      : (rawUpdatedAt ? String(rawUpdatedAt) : null);

    out.push({
      key,
      kind: typename,
      kindLabel: KIND_LABELS[typename],
      title,
      canonicalUrl,
      updatedAt,
    });
  }

  return out;
}
